import { Injectable } from '@angular/core';
import { Query, QueryService, QueryState, UserIdService } from '@spartacus/core';
import { BehaviorSubject, Observable, Subject, filter, of, switchMap, withLatestFrom } from 'rxjs';
import { Product, ProductAttributeTable, ProductCertification, ProductDetailsTopic } from '../../../../core/model';
import { ProductRelations } from '../../../../core/occ/occ-models/occ-generated.models';
import { ProductDetailsConnector } from '../connectors';
import { AssortmentTable } from '../models';

@Injectable()
export class ProductDetailsService {
  protected reloadProductTrigger$ = new Subject();
  protected productCode$ = new BehaviorSubject<string>(undefined);
  protected topics$ = new BehaviorSubject<ProductDetailsTopic[]>([]);
  protected technicalAttributesQuery = this.createProductQuery(
    (userId, productCode) => this.productDetailsConnector.getTechnicalDataTable(userId, productCode),
    ProductDetailsTopic.TECHNICAL_DATA_TABLE
  );
  protected assortmentQuery = this.createProductQuery(
    (userId, productCode) => this.productDetailsConnector.getAssortmentTable(userId, productCode),
    ProductDetailsTopic.ASSORTMENT_PACKAGING_TABLE
  );
  protected certLabelQuery = this.createProductQuery(
    (userId, productCode) => this.productDetailsConnector.getCertLabels(userId, productCode),
    ProductDetailsTopic.CERT_LABELS
  );
  protected relatedItemsQuery = this.createProductQuery(
    (userId, productCode) => this.productDetailsConnector.getRelatedItems(userId, productCode),
    ProductDetailsTopic.RELATED_ITEMS
  );

  constructor(
    protected query: QueryService,
    protected userIdService: UserIdService,
    protected productDetailsConnector: ProductDetailsConnector
  ) {}

  setProduct(product: Product): void {
    if (product?.code !== this.productCode$.getValue()) {
      this.productCode$.next(product?.code);
      this.topics$.next(product?.extraInfo?.topics ?? []);
      this.reloadProductTrigger$.next(true);
    }
  }

  getTechnicalAttributes(): Observable<ProductAttributeTable> {
    return this.technicalAttributesQuery.get();
  }

  getTechnicalAttributesState(): Observable<QueryState<ProductAttributeTable>> {
    return this.technicalAttributesQuery.getState();
  }

  getAssortmentTable(): Observable<AssortmentTable> {
    return this.assortmentQuery.get();
  }

  getAssortmentTableState(): Observable<QueryState<AssortmentTable>> {
    return this.assortmentQuery.getState();
  }

  getCertLabels(): Observable<ProductCertification> {
    return this.certLabelQuery.get();
  }

  getCertLabelsState(): Observable<QueryState<ProductCertification>> {
    return this.certLabelQuery.getState();
  }

  getRelatedItems(): Observable<ProductRelations> {
    return this.relatedItemsQuery.get();
  }

  getRelatedItemsState(): Observable<QueryState<ProductRelations>> {
    return this.relatedItemsQuery.getState();
  }

  protected createProductQuery<T>(
    fetchData: (userId: string, productCode: string) => Observable<T>,
    topic: ProductDetailsTopic
  ): Query<T> {
    return this.query.create(
      () =>
        this.userIdService.takeUserId().pipe(
          withLatestFrom(this.productCode$, this.topics$),
          filter(([, productCode]) => !!productCode),
          switchMap(([userId, productCode, topics]) => (topics.includes(topic) ? fetchData(userId, productCode) : of(undefined)))
        ),
      {
        reloadOn: [this.reloadProductTrigger$],
      }
    );
  }
}
